'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from 'sonner'
import {
  ArrowLeft, Mail, Phone, Building2, Briefcase, CalendarDays,
  CheckCircle2, XCircle, Clock, Timer, UserRound, Pencil,
} from 'lucide-react'
import { AttendanceOverrideModal } from './AttendanceOverrideModal'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { SectionHeader } from '@/components/ui/section-header'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { formatTime } from '@/lib/utils'
import { todayIST } from '@/lib/date-utils'

interface BreakEntry {
  start: string | null
  end: string | null
}

interface AttendanceRecord {
  id: string
  date: string
  checkIn: string | null
  checkOut: string | null
  status: string
  workMode: string
  notes: string | null
  breaks?: BreakEntry[]
}

export interface ProfileUser {
  id: string
  name: string
  email: string
  role: string
  employeeId: string | null
  department: string | null
  position: string | null
  phone: string | null
  isActive: boolean
  joinDate?: string | null
  assignedHrName?: string | null
  createdAt: string
}

interface EmployeeProfileProps {
  user: ProfileUser
  onBack: () => void
}

function totalBreakMinutes(breaks?: BreakEntry[]): number {
  if (!Array.isArray(breaks)) return 0
  let t = 0
  for (const b of breaks) {
    if (b.start && b.end) {
      const [sh, sm] = b.start.split(':').map(Number)
      const [eh, em] = b.end.split(':').map(Number)
      const d = (eh * 60 + em) - (sh * 60 + sm)
      if (d > 0) t += d
    }
  }
  return t
}

function workedMinutes(rec: AttendanceRecord): number {
  if (!rec.checkIn || !rec.checkOut) return 0
  const [ih, im] = rec.checkIn.split(':').map(Number)
  const [oh, om] = rec.checkOut.split(':').map(Number)
  const gross = (oh * 60 + om) - (ih * 60 + im)
  if (gross <= 0) return 0
  return Math.max(0, gross - totalBreakMinutes(rec.breaks))
}

function formatHours(mins: number): string {
  if (mins <= 0) return '—'
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function fmtDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

function DetailRow({ icon: Icon, label, value }: { icon: any; label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="h-4 w-4 mt-0.5 text-neutral-400 dark:text-white/40 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">{label}</p>
        <p className="text-sm text-neutral-900 dark:text-white/90 break-words">{value || '—'}</p>
      </div>
    </div>
  )
}

export function EmployeeProfile({ user, onBack }: EmployeeProfileProps) {
  const [records, setRecords] = useState<AttendanceRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [month, setMonth] = useState<string>(() => todayIST().slice(0, 7))
  const [editing, setEditing] = useState<AttendanceRecord | null>(null)
  // Day chosen via the "edit any day" picker — lets HR correct dates that have
  // no attendance record yet (mark a leave, holiday, absence, …).
  const [manualDate, setManualDate] = useState('')
  // Prefers the real record for the chosen date so the modal shows current
  // values; falls back to a blank day so HR can also correct unrecorded dates.
  const editingManual = manualDate
    ? records.find(r => r.date === manualDate) || { id: `${user.id}_${manualDate}`, date: manualDate, checkIn: null, checkOut: null, status: 'PRESENT', workMode: 'OFFICE', notes: null, breaks: [] }
    : null

  const fetchAttendance = useCallback(async () => {
    setLoading(true)
    try {
      const [y, m] = month.split('-').map(Number)
      const lastDay = new Date(y, m, 0).getDate()
      const params = new URLSearchParams({
        userId: user.id,
        startDate: `${month}-01`,
        endDate: `${month}-${String(lastDay).padStart(2, '0')}`,
      })
      const res = await apiFetch(`/api/attendance?${params.toString()}`)
      const data = await res.json()
      setRecords(data.attendance || [])
    } catch (e: any) {
      console.error('Error fetching employee attendance:', e)
      toast.error(e.message || 'Failed to load attendance records')
    } finally {
      setLoading(false)
    }
  }, [user.id, month])

  useEffect(() => {
    fetchAttendance()
  }, [fetchAttendance])

  const kpis = useMemo(() => {
    // Status-first: a day HR explicitly marked LEAVE/HOLIDAY/WEEKEND is not an
    // absence even without a check-in, and an HR-marked PRESENT day counts as
    // present. Unrecorded working days default to ABSENT (see attendance-store).
    const stat = (r: AttendanceRecord) => (r.status || '').toUpperCase()
    const notAbsent = (r: AttendanceRecord) => !!r.checkIn || ['PRESENT', 'LEAVE', 'HOLIDAY', 'WEEKEND'].includes(stat(r))
    const present = records.filter(notAbsent).length
    const absent = records.length - present
    const totalMins = records.reduce((sum, r) => sum + workedMinutes(r), 0)
    const complete = records.filter(r => r.checkIn && r.checkOut).length
    const avg = complete > 0 ? Math.round(totalMins / complete) : 0
    return { present, absent, totalMins, avg }
  }, [records])

  return (
    <div className="space-y-5 md:space-y-6">
      <SectionHeader
        icon={UserRound}
        title={user.name}
        description={
          `${[user.position, user.department].filter(Boolean).join(' · ') || 'No position set'}` +
          (user.employeeId ? ` · ${user.employeeId}` : '')
        }
        action={
          <>
            <StatusPill tone={user.role === 'ADMIN' ? 'orange' : user.role === 'HR' ? 'violet' : 'blue'}>
              {user.role}
            </StatusPill>
            <StatusPill tone={user.isActive ? 'green' : 'red'} dot>
              {user.isActive ? 'Active' : 'Inactive'}
            </StatusPill>
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              aria-label="Back to users"
              className="text-neutral-600 dark:text-white/60 hover:bg-neutral-100 dark:hover:bg-white/5"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </>
        }
      />

      {/* Details */}
      <GlassCard>
        <h3 className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90 mb-4">Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <DetailRow icon={Mail} label="Email" value={user.email} />
          <DetailRow icon={Phone} label="Phone" value={user.phone} />
          <DetailRow icon={Building2} label="Department" value={user.department} />
          <DetailRow icon={Briefcase} label="Position" value={user.position} />
          <DetailRow icon={CalendarDays} label="Joined" value={user.joinDate ? fmtDate(user.joinDate) : null} />
          <DetailRow icon={Briefcase} label="Assigned HR" value={user.assignedHrName} />
        </div>
      </GlassCard>

      {/* Attendance */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90">
          Attendance Records
        </h3>
        <Input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          aria-label="Attendance month"
          className="w-full sm:w-[170px]"
        />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-neutral-400 dark:text-white/40">
          Need to correct a day with no record? Pick any date and edit it.
        </p>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={manualDate}
            onChange={(e) => setManualDate(e.target.value)}
            aria-label="Pick a date to edit"
            className="w-full sm:w-[170px]"
          />
          <Button
            size="sm"
            variant="outline"
            disabled={!manualDate}
            onClick={() => editingManual && setEditing(editingManual)}
            className="border-neutral-200 text-neutral-700 dark:border-white/10 dark:text-white/70"
          >
            <Pencil className="mr-1 h-3.5 w-3.5" /> Edit day
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Days Present" value={kpis.present} icon={CheckCircle2} tone="green" />
        <KpiTile label="Days Absent" value={kpis.absent} icon={XCircle} tone="red" />
        <KpiTile label="Total Hours" value={formatHours(kpis.totalMins)} icon={Clock} tone="blue" />
        <KpiTile label="Avg / Day" value={formatHours(kpis.avg)} icon={Timer} tone="violet" />
      </div>

      <GlassCard inset>
        {loading ? (
          <TabLoader />
        ) : records.length === 0 ? (
          <EmptyState
            icon={CalendarDays}
            title="No attendance records for this month"
            description="Pick a different month to see this employee's history."
          />
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-200 dark:border-white/10 hover:bg-transparent">
                    <TableHead className="text-neutral-500 dark:text-white/50">Date</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50">Check In</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50">Check Out</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50">Breaks</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50">Hours</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50">Mode</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50">Status</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((r) => (
                    <TableRow key={r.id} className="border-neutral-100 dark:border-white/5 hover:bg-neutral-50 dark:hover:bg-white/[0.03]">
                      <TableCell className="text-neutral-900 dark:text-white/90 tabular-nums">{fmtDate(r.date)}</TableCell>
                      <TableCell className="text-neutral-800 dark:text-white/80 tabular-nums">{r.checkIn ? formatTime(r.checkIn) : '—'}</TableCell>
                      <TableCell className="text-neutral-800 dark:text-white/80 tabular-nums">{r.checkOut ? formatTime(r.checkOut) : '—'}</TableCell>
                      <TableCell className="text-neutral-700 dark:text-white/70 tabular-nums">
                        {(r.breaks || []).filter(b => b.start && b.end).length}
                      </TableCell>
                      <TableCell className="text-neutral-800 dark:text-white/80 tabular-nums">{formatHours(workedMinutes(r))}</TableCell>
                      <TableCell className="text-neutral-700 dark:text-white/70">{r.workMode || '—'}</TableCell>
                      <TableCell><StatusPill tone={statusTone(r.status)} dot>{r.status}</StatusPill></TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Edit attendance for ${r.date}`}
                          onClick={() => setEditing(r)}
                          className="h-8 w-8 text-neutral-400 hover:text-orange-600 hover:bg-orange-500/10 dark:text-white/40 dark:hover:text-orange-300"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile */}
            <div className="md:hidden space-y-3 p-3">
              {records.map((r) => (
                <GlassCard key={r.id} tone={r.checkIn ? 'green' : 'red'} className="!p-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-neutral-900 dark:text-white/90 font-medium tabular-nums">{fmtDate(r.date)}</p>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit attendance for ${r.date}`}
                        onClick={() => setEditing(r)}
                        className="h-7 w-7 text-neutral-400 hover:text-orange-600 hover:bg-orange-500/10 dark:text-white/40 dark:hover:text-orange-300"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <StatusPill tone={statusTone(r.status)} dot>{r.status}</StatusPill>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-neutral-400 dark:text-white/40">In</p>
                      <p className="text-neutral-800 dark:text-white/80 tabular-nums">{r.checkIn ? formatTime(r.checkIn) : '—'}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400 dark:text-white/40">Out</p>
                      <p className="text-neutral-800 dark:text-white/80 tabular-nums">{r.checkOut ? formatTime(r.checkOut) : '—'}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400 dark:text-white/40">Hours</p>
                      <p className="text-neutral-800 dark:text-white/80 tabular-nums">{formatHours(workedMinutes(r))}</p>
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>
          </>
        )}
      </GlassCard>

      {editing && (
        <AttendanceOverrideModal
          open={true}
          onOpenChange={(o) => !o && setEditing(null)}
          userId={user.id}
          userName={user.name}
          date={editing.date}
          currentCheckIn={editing.checkIn}
          currentCheckOut={editing.checkOut}
          currentBreaks={editing.breaks}
          currentWorkMode={editing.workMode}
          currentStatus={editing.status}
          onSaved={fetchAttendance}
        />
      )}
    </div>
  )
}
